import { MEASUREMENT_UNITS, type MeasurementUnit } from './measurement-unit';

// Common Italian spellings and abbreviations for each unit, as they appear in
// free-text recipe imports (AI extraction from supplier documents, manual
// paste from a spreadsheet). A `Record<MeasurementUnit, ...>` forces every
// unit to declare at least its own synonym list at compile time.
const MEASUREMENT_UNIT_SYNONYMS: Record<MeasurementUnit, readonly string[]> = {
  G: ['g', 'gr', 'gr.', 'grammo', 'grammi'],
  HG: ['hg', 'etto', 'etti', 'ettogrammo', 'ettogrammi'],
  KG: ['kg', 'kilo', 'kili', 'chilo', 'chili', 'chilogrammo', 'chilogrammi'],
  LITRI: ['l', 'lt', 'litro', 'litri'],
  ML: ['ml', 'millilitro', 'millilitri'],
  CL: ['cl', 'centilitro', 'centilitri'],
  PZ: ['pz', 'pezzo', 'pezzi'],
  CONF: ['conf', 'confezione', 'confezioni'],
  BOTTIGLIE: ['bott', 'bottiglia', 'bottiglie'],
  BUSTE: ['busta', 'buste'],
  VASCHETTE: ['vaschetta', 'vaschette'],
  BARATTOLI: ['barattolo', 'barattoli'],
  LATTINE: ['lattina', 'lattine'],
  SCATOLE: ['scatola', 'scatole'],
  SACCHI: ['sacco', 'sacchi'],
  CASSETTE: ['cassetta', 'cassette'],
  BANCALI: ['bancale', 'bancali', 'pallet'],
  M: ['m', 'metro', 'metri'],
  CM: ['cm', 'centimetro', 'centimetri'],
  MQ: ['mq', 'm2', 'metro quadro', 'metri quadri', 'metro quadrato', 'metri quadrati'],
  MC: ['mc', 'm3', 'metro cubo', 'metri cubi'],
};

const SYNONYM_TO_MEASUREMENT_UNIT = buildSynonymLookup();

function buildSynonymLookup(): Map<string, MeasurementUnit> {
  const lookup = new Map<string, MeasurementUnit>();
  for (const unit of MEASUREMENT_UNITS) {
    for (const synonym of MEASUREMENT_UNIT_SYNONYMS[unit]) {
      lookup.set(normalizeMeasurementUnitToken(synonym), unit);
    }
  }
  return lookup;
}

// Lowercases, strips accents, trims, and drops leading/trailing punctuation
// so "Kg.", " LITRI ", "pz," and "litri" all collapse onto the same lookup
// key. Internal spaces are preserved (collapsed to a single space) because
// multi-word synonyms like "metro quadro" need them.
function normalizeMeasurementUnitToken(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    .replace(/\s+/g, ' ');
}

/**
 * Resolves free-text (Italian, case-insensitive, tolerant of spaces and
 * punctuation) to a `MeasurementUnit`, for AI-assisted recipe import where
 * the source text spells out units informally ("gr", "chili", "etto", "pz").
 *
 * Returns `null` when the text does not match a known unit. This function
 * never guesses a default: an unrecognized unit is the caller's decision to
 * make (reject the row, ask for confirmation, fall back to a specific unit),
 * never a silent one made here.
 */
export function normalizeMeasurementUnitText(text: string): MeasurementUnit | null {
  const normalizedToken = normalizeMeasurementUnitToken(text);
  if (normalizedToken.length === 0) {
    return null;
  }
  return SYNONYM_TO_MEASUREMENT_UNIT.get(normalizedToken) ?? null;
}
